/**
 * ExpertProfilesPage – Experten-Profil Modul.
 * Tab 1: "Mein Profil" — eigenes Experten-Profil erstellen und pflegen (nur wenn Berechtigung).
 * Tab 2: "Alle Profile" — alle Experten-Profile durchsuchen.
 * Tab 3: "Berechtigungen" — Modul-Berechtigungen verwalten (PLATFORM_ADMIN oder Modul-Admin).
 *
 * Wenn der User keine Berechtigung hat (403 auf /expert-profile/me),
 * wird nur der "Alle Profile"-Tab angezeigt.
 */
import { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import api from '../api/client';
import * as epApi from './api';
import type { ExpertProfile } from './types';
import { UserAvatar } from '../components/UserAvatar';
import { ExpertProfileTab } from './ExpertProfileTab';
import styles from './ExpertProfilesPage.module.css';
import { AdminProfileAccessPage } from '../admin/AdminProfileAccessPage';

type ModuleTab = 'my-profile' | 'all-profiles' | 'permissions' | 'profile-access';

interface UserInfo {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  jobTitle?: string | null;
  department?: string | null;
  avatar?: string | null;
}

interface Permission {
  id: string;
  key: string;
  name: string;
  description?: string;
  scope: string;
  moduleKey?: string;
}

interface PermissionGroup {
  id: string;
  name: string;
  description?: string;
  color?: string;
  isSystem: boolean;
  groupPermissions: { permission: Permission }[];
  userGroups?: { user: UserInfo }[];
  _count?: { userGroups: number };
}

export function ExpertProfilesPage() {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [search, setSearch] = useState('');

  const isPlatformAdmin = currentUser?.role === 'PLATFORM_ADMIN';
  const canManagePermissions = isPlatformAdmin;

  // Permission-Check: Kann der User sein eigenes Profil bearbeiten?
  const [canEditOwn, setCanEditOwn] = useState<boolean | null>(null); // null = noch pruefend

  useEffect(() => {
    let cancelled = false;
    api.get('/expert-profile/me')
      .then(() => {
        if (!cancelled) setCanEditOwn(true);
      })
      .catch((err: any) => {
        if (!cancelled) {
          const status = err?.response?.status;
          setCanEditOwn(status === 403 ? false : true);
        }
      });
    return () => { cancelled = true; };
  }, []);

  // Default-Tab: "Alle Profile" zuerst
  const [activeTab, setActiveTab] = useState<ModuleTab>('all-profiles');

  // Wenn Permission-Check fertig und kein Zugriff → bleibt auf "Alle Profile" (default)

  // Alle User laden (ueber Core-Service) — nur fuer Berechtigungen-Tab
  const { data: users } = useQuery<UserInfo[]>({
    queryKey: ['users-list'],
    queryFn: async () => {
      const { data } = await api.get('/users', { params: { limit: 500 } });
      return data.data ?? data;
    },
    enabled: activeTab === 'permissions',
  });

  // Alle Experten-Profile laden (bereits mit User-Daten angereichert vom Backend)
  const { data: profiles, isLoading } = useQuery<(ExpertProfile & { user?: UserInfo | null })[]>({
    queryKey: ['expert-profiles'],
    queryFn: epApi.getAllProfiles,
    enabled: activeTab === 'all-profiles',
  });

  // RBAC: Gruppen und Permissions laden (fuer Berechtigungen-Tab)
  const { data: groups, refetch: refetchGroups } = useQuery<PermissionGroup[]>({
    queryKey: ['admin', 'rbac', 'groups'],
    queryFn: async () => {
      const res = await api.get('/rbac/groups');
      return res.data;
    },
    enabled: activeTab === 'permissions' && canManagePermissions,
  });

  const { data: allPermissions, isLoading: permsLoading, error: permsError } = useQuery<Permission[]>({
    queryKey: ['admin', 'rbac', 'permissions', 'module', 'expert-profile'],
    queryFn: async () => {
      const res = await api.get('/rbac/permissions', { params: { scope: 'MODULE' } });
      return res.data;
    },
    enabled: activeTab === 'permissions' && canManagePermissions,
  });

  // Nur Modul-Permissions des Expert-Profile-Moduls
  const modulePermissions = (allPermissions ?? []).filter(
    (p) => p.moduleKey === 'expert-profile',
  );

  // Profile sind bereits mit User-Daten angereichert vom Backend
  const enriched =
    profiles?.map((p) => ({
      ...p,
      user: p.user || undefined,
    })) ?? [];

  // Suche
  const filtered = enriched.filter((p) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    const u = p.user;
    return (
      u?.firstName?.toLowerCase().includes(q) ||
      u?.lastName?.toLowerCase().includes(q) ||
      u?.email?.toLowerCase().includes(q) ||
      u?.department?.toLowerCase().includes(q) ||
      u?.jobTitle?.toLowerCase().includes(q) ||
      p.skills.some((s) => (typeof s === 'string' ? s : s.name).toLowerCase().includes(q))
    );
  });

  const [exporting, setExporting] = useState(false);

  const handleExportOwn = async (format: 'pdf' | 'docx') => {
    setExporting(true);
    try {
      const url = format === 'pdf' ? epApi.getMyExportPdfUrl() : epApi.getMyExportDocxUrl();
      const response = await api.get(url, { responseType: 'blob' });
      const mimeType = format === 'pdf'
        ? 'application/pdf'
        : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      const blob = new Blob([response.data], { type: mimeType });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `Expertenprofil_${currentUser?.lastName ?? 'export'}.${format}`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      // Export fehlgeschlagen
    } finally {
      setExporting(false);
    }
  };

  const handleExport = async (userId: string, format: 'pdf' | 'docx', lastName?: string) => {
    try {
      const url =
        format === 'pdf'
          ? epApi.getUserExportPdfUrl(userId)
          : epApi.getUserExportDocxUrl(userId);
      const response = await api.get(url, { responseType: 'blob' });
      const mimeType = format === 'pdf'
        ? 'application/pdf'
        : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      const blob = new Blob([response.data], { type: mimeType });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `Expertenprofil_${lastName ?? 'export'}.${format}`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      // Export fehlgeschlagen
    }
  };

  // Noch am Pruefen
  if (canEditOwn === null) {
    return (
      <div className={styles.page}>
        <div className={styles.pageHeader}>
        <h1 className={styles.pageHeaderTitle}>Experten Profile</h1>
      </div>
        <p className={styles.loading}>Berechtigungen werden geprüft...</p>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <h1 className={styles.pageHeaderTitle}>Experten Profile</h1>
      </div>

      {/* Tab-Leiste mit Export-Buttons */}
      <div className={styles.tabBar}>
        <div className={styles.tabs}>
          <button
            type="button"
            className={`${styles.tab} ${activeTab === 'all-profiles' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('all-profiles')}
          >
            Alle Profile
          </button>
          {canEditOwn && (
            <button
              type="button"
              className={`${styles.tab} ${activeTab === 'my-profile' ? styles.tabActive : ''}`}
              onClick={() => setActiveTab('my-profile')}
            >
              Mein Experten Profil
            </button>
          )}
          {canManagePermissions && (
            <button
              type="button"
              className={`${styles.tab} ${activeTab === 'permissions' ? styles.tabActive : ''}`}
              onClick={() => setActiveTab('permissions')}
            >
              Berechtigungen
            </button>
          )}
          {canManagePermissions && (
            <button
              type="button"
              className={`${styles.tab} ${activeTab === 'profile-access' ? styles.tabActive : ''}`}
              onClick={() => setActiveTab('profile-access')}
            >
              Profilzugriff
            </button>
          )}
        </div>
        {activeTab === 'my-profile' && canEditOwn && (
          <div className={styles.tabBarActions}>
            <button
              type="button"
              className={styles.exportBtn}
              onClick={() => handleExportOwn('pdf')}
              disabled={exporting}
            >
              {exporting ? 'Exportiert...' : 'PDF Export'}
            </button>
            <button
              type="button"
              className={styles.exportBtn}
              onClick={() => handleExportOwn('docx')}
              disabled={exporting}
            >
              {exporting ? 'Exportiert...' : 'Word Export'}
            </button>
          </div>
        )}
      </div>

      {/* Tab: Mein Profil */}
      {activeTab === 'my-profile' && canEditOwn && (
        <div className={styles.tabContent}>
          <ExpertProfileTab />
        </div>
      )}

      {/* Tab: Alle Profile */}
      {activeTab === 'all-profiles' && (
        <div className={styles.tabContent}>
          <div className={styles.searchBar}>
            <div className={styles.searchBox}>
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <circle cx="7" cy="7" r="5" />
                <path d="M11 11l3.5 3.5" />
              </svg>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Suchen nach Name, Abteilung, Skill..."
                className={styles.searchInput}
              />
            </div>
          </div>

          {isLoading ? (
            <p className={styles.loading}>Profile werden geladen...</p>
          ) : filtered.length === 0 ? (
            <p className={styles.empty}>Keine Experten-Profile gefunden.</p>
          ) : (
            <div className={styles.grid}>
              {filtered.map((p) => (
                <div
                  key={p.id}
                  className={styles.card}
                  onClick={() => navigate(`/expert-profiles/${p.userId}`)}
                >
                  {(isPlatformAdmin || p.userId === currentUser?.id) && (
                    <button
                      className={styles.cardEditBtn}
                      onClick={(e) => { e.stopPropagation(); navigate(`/expert-profiles/${p.userId}?edit=1`); }}
                      title="Profil bearbeiten"
                    >
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 1l3 3-9 9H3v-3z" />
                      </svg>
                    </button>
                  )}
                  <div className={styles.cardHeader}>
                    <UserAvatar
                      firstName={p.user?.firstName ?? '?'}
                      lastName={p.user?.lastName ?? ''}
                      avatar={p.user?.avatar}
                      size={48}
                    />
                    <div className={styles.cardInfo}>
                      <h3 className={styles.cardName}>
                        {p.user?.firstName} {p.user?.lastName}
                      </h3>
                      {p.user?.jobTitle && (
                        <span className={styles.cardJob}>{p.user.jobTitle}</span>
                      )}
                      {p.user?.department && (
                        <span className={styles.cardDept}>{p.user.department}</span>
                      )}
                    </div>
                  </div>

                  {/* Skills */}
                  {p.skills.length > 0 && (
                    <div className={styles.cardSkills}>
                      {p.skills.slice(0, 6).map((s) => {
                        const name = typeof s === 'string' ? s : s.name;
                        return (
                          <span key={name} className={styles.skillChip}>
                            {name}
                          </span>
                        );
                      })}
                      {p.skills.length > 6 && (
                        <span className={styles.skillMore}>+{p.skills.length - 6}</span>
                      )}
                    </div>
                  )}

                  {/* Stats */}
                  <div className={styles.cardStats}>
                    <span title="Projekte">📁 {p.projects.length}</span>
                    <span title="Erfahrungsbereiche">💼 {p.experiences.length}</span>
                    <span title="Zertifikate">🎓 {p.certifications.length}</span>
                    <span title="Sprachen">🌐 {p.languages.length}</span>
                  </div>

                  {/* Export-Buttons */}
                  <div className={styles.cardActions}>
                    <button
                      className={styles.exportBtnSmall}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleExport(p.userId, 'pdf', p.user?.lastName);
                      }}
                      title="PDF exportieren"
                    >
                      PDF
                    </button>
                    <button
                      className={styles.exportBtnSmall}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleExport(p.userId, 'docx', p.user?.lastName);
                      }}
                      title="Word exportieren"
                    >
                      DOCX
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tab: Berechtigungen */}
      {activeTab === 'permissions' && canManagePermissions && (
        <div className={styles.tabContent}>
          {permsLoading ? (
            <p className={styles.loading}>Berechtigungen werden geladen...</p>
          ) : permsError ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#dc2626' }}>
              <p>Fehler beim Laden der Berechtigungen.</p>
              <p style={{ fontSize: '0.75rem', marginTop: '0.5rem' }}>{String(permsError)}</p>
            </div>
          ) : (
            <ModulePermissionsTab
              groups={groups ?? []}
              modulePermissions={modulePermissions}
              users={users ?? []}
              onRefresh={() => refetchGroups()}
              isPlatformAdmin={isPlatformAdmin}
            />
          )}
        </div>
      )}

      {/* Tab: Profilzugriff */}
      {activeTab === 'profile-access' && canManagePermissions && (
        <div className={styles.tabContent}>
          <AdminProfileAccessPage />
        </div>
      )}
    </div>
  );
}

// ---- Berechtigungen-Tab fuer das Experten-Modul ----

const PERM_LABELS: Record<string, { label: string; description: string }> = {
  'expert-profile.view': {
    label: 'Profile anzeigen',
    description: 'Alle Experten-Profile in der Übersicht sehen',
  },
  'expert-profile.export': {
    label: 'Profile exportieren',
    description: 'Experten-Profile als PDF oder Word exportieren',
  },
  'expert-profile.edit-own': {
    label: 'Eigenes Profil bearbeiten',
    description: 'Das eigene Experten-Profil erstellen und pflegen',
  },
  'expert-profile.edit-all': {
    label: 'Alle Profile bearbeiten',
    description: 'Experten-Profile aller Benutzer bearbeiten',
  },
};

const GROUP_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'];

function ModulePermissionsTab({
  groups,
  modulePermissions,
  users,
  onRefresh,
}: {
  groups: PermissionGroup[];
  modulePermissions: Permission[];
  users: UserInfo[];
  onRefresh: () => void;
  isPlatformAdmin?: boolean;
}) {
  const [editingGroup, setEditingGroup] = useState<PermissionGroup | null>(null);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [showAddUsers, setShowAddUsers] = useState<string | null>(null); // groupId

  // Gruppen die mindestens eine Experten-Modul-Berechtigung haben
  const relevantGroups = groups.filter((g) =>
    g.groupPermissions.some((gp) => gp.permission.moduleKey === 'expert-profile'),
  );

  // ---- Mutations ----

  const createGroupMutation = useMutation({
    mutationFn: async (data: { name: string; description?: string; color?: string; permissionIds: string[] }) => {
      const res = await api.post('/rbac/groups', data);
      return res.data;
    },
    onSuccess: () => {
      onRefresh();
      setShowCreateGroup(false);
    },
  });

  const updateGroupMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: { name?: string; description?: string; color?: string; permissionIds?: string[] } }) => {
      const res = await api.patch(`/rbac/groups/${id}`, data);
      return res.data;
    },
    onSuccess: () => {
      onRefresh();
      setEditingGroup(null);
    },
  });

  const deleteGroupMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/rbac/groups/${id}`);
    },
    onSuccess: () => {
      onRefresh();
    },
  });

  const addUserToGroupMutation = useMutation({
    mutationFn: async ({ groupId, userId }: { groupId: string; userId: string }) => {
      await api.post(`/rbac/groups/${groupId}/users`, { userId });
    },
    onSuccess: () => {
      onRefresh();
    },
  });

  const removeUserFromGroupMutation = useMutation({
    mutationFn: async ({ groupId, userId }: { groupId: string; userId: string }) => {
      await api.delete(`/rbac/groups/${groupId}/users/${userId}`);
    },
    onSuccess: () => {
      onRefresh();
    },
  });

  if (modulePermissions.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem 0', color: 'var(--color-text-muted)' }}>
        <p style={{ fontSize: '0.9375rem' }}>Keine Modul-Berechtigungen registriert.</p>
        <p style={{ fontSize: '0.8125rem', marginTop: '0.5rem' }}>
          Der Expert-Profile-Service muss die Berechtigungen beim Core-Service registrieren.
        </p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {/* Aktions-Leiste */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h3 style={{ fontSize: '1rem', fontWeight: 600, margin: 0 }}>
            Berechtigungsgruppen
          </h3>
          <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)', margin: '0.25rem 0 0' }}>
            Verwalten Sie Gruppen und deren Berechtigungen fuer das Experten-Modul.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowCreateGroup(true)}
          style={{
            padding: '0.5rem 1rem',
            borderRadius: '0.375rem',
            border: 'none',
            background: 'var(--color-primary, #3B82F6)',
            color: '#fff',
            fontSize: '0.8125rem',
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          + Neue Gruppe
        </button>
      </div>

      {/* Gruppe erstellen Dialog */}
      {showCreateGroup && (
        <GroupForm
          modulePermissions={modulePermissions}
          onSave={(data) => createGroupMutation.mutate(data)}
          onCancel={() => setShowCreateGroup(false)}
          saving={createGroupMutation.isPending}
        />
      )}

      {/* Gruppe bearbeiten Dialog */}
      {editingGroup && (
        <GroupForm
          group={editingGroup}
          modulePermissions={modulePermissions}
          onSave={(data) => updateGroupMutation.mutate({ id: editingGroup.id, data })}
          onCancel={() => setEditingGroup(null)}
          saving={updateGroupMutation.isPending}
        />
      )}

      {/* Gruppen-Karten */}
      {relevantGroups.length === 0 && !showCreateGroup ? (
        <div style={{
          background: 'var(--color-bg-card)',
          borderRadius: 'var(--radius-md)',
          boxShadow: 'var(--shadow-sm)',
          border: '1px solid var(--color-border)',
          padding: '2rem',
          textAlign: 'center',
          color: 'var(--color-text-muted)',
        }}>
          <p style={{ fontSize: '0.875rem' }}>
            Noch keine Gruppen mit Experten-Modul-Berechtigungen konfiguriert.
          </p>
          <p style={{ fontSize: '0.8125rem', marginTop: '0.25rem' }}>
            Erstellen Sie eine neue Gruppe, um Berechtigungen fuer dieses Modul zu vergeben.
          </p>
        </div>
      ) : (
        relevantGroups.map((group) => (
          <div
            key={group.id}
            style={{
              background: 'var(--color-bg-card)',
              borderRadius: 'var(--radius-md)',
              boxShadow: 'var(--shadow-sm)',
              border: '1px solid var(--color-border)',
              overflow: 'hidden',
            }}
          >
            {/* Gruppen-Header */}
            <div style={{
              padding: '1rem 1.25rem',
              borderBottom: '1px solid var(--color-border)',
              background: 'var(--color-bg)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                {group.color && (
                  <span style={{
                    width: 12, height: 12, borderRadius: '50%',
                    background: group.color, display: 'inline-block',
                  }} />
                )}
                <div>
                  <h4 style={{ fontSize: '0.9375rem', fontWeight: 600, margin: 0 }}>
                    {group.name}
                    {group.isSystem && (
                      <span style={{
                        marginLeft: '0.5rem', padding: '0.125rem 0.375rem',
                        borderRadius: '9999px', fontSize: '0.6875rem',
                        background: '#e0e7ff', color: '#3730a3',
                      }}>
                        System
                      </span>
                    )}
                  </h4>
                  {group.description && (
                    <p style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', margin: '0.125rem 0 0' }}>
                      {group.description}
                    </p>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  type="button"
                  onClick={() => setEditingGroup(group)}
                  style={{
                    padding: '0.375rem 0.75rem', borderRadius: '0.25rem',
                    border: '1px solid var(--color-border)', background: 'var(--color-bg-card)',
                    fontSize: '0.75rem', cursor: 'pointer', color: 'var(--color-text)',
                  }}
                >
                  Bearbeiten
                </button>
                {!group.isSystem && (
                  <button
                    type="button"
                    onClick={() => {
                      if (confirm(`Gruppe "${group.name}" wirklich löschen?`)) {
                        deleteGroupMutation.mutate(group.id);
                      }
                    }}
                    style={{
                      padding: '0.375rem 0.75rem', borderRadius: '0.25rem',
                      border: '1px solid #fecaca', background: '#fef2f2',
                      fontSize: '0.75rem', cursor: 'pointer', color: '#dc2626',
                    }}
                  >
                    Löschen
                  </button>
                )}
              </div>
            </div>

            {/* Berechtigungen der Gruppe */}
            <div style={{ padding: '0.75rem 1.25rem', borderBottom: '1px solid var(--color-border)' }}>
              <div style={{ fontSize: '0.6875rem', textTransform: 'uppercase', color: 'var(--color-text-muted)', marginBottom: '0.375rem', fontWeight: 600 }}>
                Berechtigungen
              </div>
              <div style={{ display: 'flex', gap: '0.375rem', flexWrap: 'wrap' }}>
                {modulePermissions.map((perm) => {
                  const hasPerm = group.groupPermissions.some(
                    (gp) => gp.permission.key === perm.key,
                  );
                  return (
                    <span
                      key={perm.id}
                      style={{
                        padding: '0.25rem 0.625rem',
                        borderRadius: '9999px',
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        background: hasPerm ? '#dcfce7' : '#f3f4f6',
                        color: hasPerm ? '#166534' : '#9ca3af',
                        border: `1px solid ${hasPerm ? '#bbf7d0' : '#e5e7eb'}`,
                      }}
                    >
                      {hasPerm ? '✓ ' : ''}{PERM_LABELS[perm.key]?.label ?? perm.name}
                    </span>
                  );
                })}
              </div>
            </div>

            {/* Benutzer in der Gruppe */}
            <div style={{ padding: '0.75rem 1.25rem' }}>
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                marginBottom: '0.5rem',
              }}>
                <div style={{ fontSize: '0.6875rem', textTransform: 'uppercase', color: 'var(--color-text-muted)', fontWeight: 600 }}>
                  Mitglieder ({group._count?.userGroups ?? group.userGroups?.length ?? 0})
                </div>
                <button
                  type="button"
                  onClick={() => setShowAddUsers(showAddUsers === group.id ? null : group.id)}
                  style={{
                    padding: '0.25rem 0.5rem', borderRadius: '0.25rem',
                    border: '1px solid var(--color-border)', background: 'var(--color-bg-card)',
                    fontSize: '0.6875rem', cursor: 'pointer', color: 'var(--color-text)',
                  }}
                >
                  + Benutzer hinzufügen
                </button>
              </div>

              {/* User hinzufügen Dropdown */}
              {showAddUsers === group.id && (
                <AddUserDropdown
                  users={users}
                  existingUserIds={new Set((group.userGroups ?? []).map((ug) => ug.user.id))}
                  onAdd={(userId) => addUserToGroupMutation.mutate({ groupId: group.id, userId })}
                  onClose={() => setShowAddUsers(null)}
                />
              )}

              {/* Bestehende Mitglieder */}
              {(group.userGroups ?? []).length === 0 ? (
                <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)', fontStyle: 'italic', margin: '0.25rem 0' }}>
                  Keine Mitglieder in dieser Gruppe.
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  {(group.userGroups ?? []).map((ug) => (
                    <div
                      key={ug.user.id}
                      style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '0.375rem 0.5rem', borderRadius: '0.25rem',
                        background: 'var(--color-bg)',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <UserAvatar
                          firstName={ug.user.firstName}
                          lastName={ug.user.lastName}
                          avatar={ug.user.avatar}
                          size={24}
                        />
                        <span style={{ fontSize: '0.8125rem' }}>
                          {ug.user.firstName} {ug.user.lastName}
                        </span>
                        <span style={{ fontSize: '0.6875rem', color: 'var(--color-text-muted)' }}>
                          {ug.user.email}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeUserFromGroupMutation.mutate({ groupId: group.id, userId: ug.user.id })}
                        style={{
                          padding: '0.125rem 0.375rem', borderRadius: '0.25rem',
                          border: 'none', background: 'transparent',
                          fontSize: '0.75rem', cursor: 'pointer', color: '#dc2626',
                        }}
                        title="Aus Gruppe entfernen"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))
      )}

      {/* Übersichts-Matrix: Alle Berechtigungen nach Gruppen */}
      {relevantGroups.length > 0 && (
        <div style={{
          background: 'var(--color-bg-card)',
          borderRadius: 'var(--radius-md)',
          boxShadow: 'var(--shadow-sm)',
          border: '1px solid var(--color-border)',
          overflow: 'hidden',
        }}>
          <div style={{
            padding: '1rem 1.25rem',
            borderBottom: '1px solid var(--color-border)',
            background: 'var(--color-bg)',
          }}>
            <h3 style={{ fontSize: '1rem', fontWeight: 600, margin: 0 }}>
              Berechtigungs-Matrix
            </h3>
            <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)', margin: '0.25rem 0 0' }}>
              Übersicht aller Berechtigungen nach Gruppen.
            </p>
          </div>

          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                <th style={{
                  padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem',
                  textTransform: 'uppercase', color: 'var(--color-text-muted)', whiteSpace: 'nowrap',
                }}>
                  Berechtigung
                </th>
                {relevantGroups.map((g) => (
                  <th key={g.id} style={{
                    padding: '0.75rem 0.75rem', textAlign: 'center', fontSize: '0.75rem',
                    textTransform: 'uppercase', color: 'var(--color-text-muted)', whiteSpace: 'nowrap',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.375rem' }}>
                      {g.color && <span style={{ width: 8, height: 8, borderRadius: '50%', background: g.color, display: 'inline-block' }} />}
                      {g.name}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {modulePermissions.map((perm) => (
                <tr key={perm.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <div style={{ fontSize: '0.875rem', fontWeight: 500 }}>
                      {PERM_LABELS[perm.key]?.label ?? perm.name}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                      {PERM_LABELS[perm.key]?.description ?? perm.key}
                    </div>
                  </td>
                  {relevantGroups.map((group) => {
                    const hasPerm = group.groupPermissions.some(
                      (gp) => gp.permission.key === perm.key,
                    );
                    return (
                      <td key={group.id} style={{ padding: '0.75rem 0.75rem', textAlign: 'center' }}>
                        {hasPerm ? (
                          <span style={{
                            display: 'inline-flex', width: 22, height: 22,
                            alignItems: 'center', justifyContent: 'center',
                            borderRadius: '50%', background: '#dcfce7', color: '#166534',
                            fontSize: '0.8125rem', fontWeight: 700,
                          }}>
                            ✓
                          </span>
                        ) : (
                          <span style={{
                            display: 'inline-flex', width: 22, height: 22,
                            alignItems: 'center', justifyContent: 'center',
                            borderRadius: '50%', background: '#f3f4f6', color: '#9ca3af',
                            fontSize: '0.75rem',
                          }}>
                            —
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---- Gruppen-Formular (Create/Edit) ----

function GroupForm({
  group,
  modulePermissions,
  onSave,
  onCancel,
  saving,
}: {
  group?: PermissionGroup;
  modulePermissions: Permission[];
  onSave: (data: { name: string; description?: string; color?: string; permissionIds: string[] }) => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const [name, setName] = useState(group?.name ?? '');
  const [description, setDescription] = useState(group?.description ?? '');
  const [color, setColor] = useState(group?.color ?? GROUP_COLORS[0]);
  const [selectedPerms, setSelectedPerms] = useState<Set<string>>(
    new Set(group?.groupPermissions.map((gp) => gp.permission.id) ?? []),
  );

  const togglePerm = (id: string) => {
    setSelectedPerms((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSubmit = () => {
    if (!name.trim()) return;
    onSave({
      name: name.trim(),
      description: description.trim() || undefined,
      color,
      permissionIds: Array.from(selectedPerms),
    });
  };

  return (
    <div style={{
      background: 'var(--color-bg-card)',
      borderRadius: 'var(--radius-md)',
      boxShadow: 'var(--shadow-md, var(--shadow-sm))',
      border: '1px solid var(--color-primary, #3B82F6)',
      padding: '1.25rem',
    }}>
      <h4 style={{ fontSize: '0.9375rem', fontWeight: 600, margin: '0 0 1rem' }}>
        {group ? `Gruppe "${group.name}" bearbeiten` : 'Neue Berechtigungsgruppe'}
      </h4>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {/* Name */}
        <div>
          <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-text-muted)', display: 'block', marginBottom: '0.25rem' }}>
            Name *
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="z.B. Experten-Bearbeiter"
            style={{
              width: '100%', padding: '0.5rem 0.75rem',
              borderRadius: '0.375rem', border: '1px solid var(--color-border)',
              fontSize: '0.875rem', background: 'var(--color-bg)',
              color: 'var(--color-text)',
              boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Beschreibung */}
        <div>
          <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-text-muted)', display: 'block', marginBottom: '0.25rem' }}>
            Beschreibung
          </label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optionale Beschreibung"
            style={{
              width: '100%', padding: '0.5rem 0.75rem',
              borderRadius: '0.375rem', border: '1px solid var(--color-border)',
              fontSize: '0.875rem', background: 'var(--color-bg)',
              color: 'var(--color-text)',
              boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Farbe */}
        <div>
          <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-text-muted)', display: 'block', marginBottom: '0.25rem' }}>
            Farbe
          </label>
          <div style={{ display: 'flex', gap: '0.375rem' }}>
            {GROUP_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setColor(c)}
                style={{
                  width: 24, height: 24, borderRadius: '50%',
                  background: c, border: color === c ? '2px solid var(--color-text)' : '2px solid transparent',
                  cursor: 'pointer', padding: 0,
                }}
              />
            ))}
          </div>
        </div>

        {/* Berechtigungen */}
        <div>
          <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-text-muted)', display: 'block', marginBottom: '0.375rem' }}>
            Modul-Berechtigungen
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
            {modulePermissions.map((perm) => (
              <label
                key={perm.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                  padding: '0.5rem 0.75rem', borderRadius: '0.375rem',
                  border: '1px solid var(--color-border)',
                  background: selectedPerms.has(perm.id) ? '#f0fdf4' : 'var(--color-bg)',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedPerms.has(perm.id)}
                  onChange={() => togglePerm(perm.id)}
                  style={{ accentColor: 'var(--color-primary, #3B82F6)' }}
                />
                <div>
                  <div style={{ fontSize: '0.875rem', fontWeight: 500 }}>
                    {PERM_LABELS[perm.key]?.label ?? perm.name}
                  </div>
                  <div style={{ fontSize: '0.6875rem', color: 'var(--color-text-muted)' }}>
                    {PERM_LABELS[perm.key]?.description ?? perm.key}
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Buttons */}
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: '0.5rem 1rem', borderRadius: '0.375rem',
              border: '1px solid var(--color-border)', background: 'var(--color-bg-card)',
              fontSize: '0.8125rem', cursor: 'pointer', color: 'var(--color-text)',
            }}
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving || !name.trim()}
            style={{
              padding: '0.5rem 1rem', borderRadius: '0.375rem',
              border: 'none', background: 'var(--color-primary, #3B82F6)',
              color: '#fff', fontSize: '0.8125rem', fontWeight: 500,
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving || !name.trim() ? 0.6 : 1,
            }}
          >
            {saving ? 'Speichern...' : group ? 'Aktualisieren' : 'Erstellen'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- Benutzer-hinzufügen Dropdown ----

function AddUserDropdown({
  users,
  existingUserIds,
  onAdd,
  onClose,
}: {
  users: UserInfo[];
  existingUserIds: Set<string>;
  onAdd: (userId: string) => void;
  onClose: () => void;
}) {
  const [search, setSearch] = useState('');

  const available = users.filter((u) => {
    if (existingUserIds.has(u.id)) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      u.firstName.toLowerCase().includes(q) ||
      u.lastName.toLowerCase().includes(q) ||
      u.email.toLowerCase().includes(q)
    );
  });

  return (
    <div style={{
      marginBottom: '0.75rem', padding: '0.75rem',
      borderRadius: '0.375rem', border: '1px solid var(--color-border)',
      background: 'var(--color-bg)',
    }}>
      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Benutzer suchen..."
        style={{
          width: '100%', padding: '0.375rem 0.625rem',
          borderRadius: '0.25rem', border: '1px solid var(--color-border)',
          fontSize: '0.8125rem', marginBottom: '0.5rem',
          background: 'var(--color-bg-card)', color: 'var(--color-text)',
          boxSizing: 'border-box',
        }}
        autoFocus
      />
      <div style={{ maxHeight: '200px', overflow: 'auto' }}>
        {available.length === 0 ? (
          <p style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', textAlign: 'center', padding: '0.5rem' }}>
            {search ? 'Kein Benutzer gefunden.' : 'Alle Benutzer sind bereits Mitglied.'}
          </p>
        ) : (
          available.slice(0, 20).map((u) => (
            <button
              key={u.id}
              type="button"
              onClick={() => onAdd(u.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                width: '100%', padding: '0.375rem 0.5rem',
                borderRadius: '0.25rem', border: 'none',
                background: 'transparent', cursor: 'pointer',
                textAlign: 'left', fontSize: '0.8125rem',
                color: 'var(--color-text)',
              }}
              onMouseOver={(e) => (e.currentTarget.style.background = 'var(--color-bg-card)')}
              onMouseOut={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              <UserAvatar firstName={u.firstName} lastName={u.lastName} avatar={u.avatar} size={22} />
              <span>{u.firstName} {u.lastName}</span>
              <span style={{ fontSize: '0.6875rem', color: 'var(--color-text-muted)' }}>{u.email}</span>
            </button>
          ))
        )}
      </div>
      <div style={{ textAlign: 'right', marginTop: '0.375rem' }}>
        <button
          type="button"
          onClick={onClose}
          style={{
            padding: '0.25rem 0.5rem', borderRadius: '0.25rem',
            border: '1px solid var(--color-border)', background: 'var(--color-bg-card)',
            fontSize: '0.6875rem', cursor: 'pointer', color: 'var(--color-text)',
          }}
        >
          Schließen
        </button>
      </div>
    </div>
  );
}
